import { inject, Injectable } from '@angular/core';
import { AuthPayload } from './models/auth.payload';
import { environment } from '../../../../environments/environment';
import { TokenDto } from './models/token.dto';
import { LocalStorage } from '../local-storage/local-storage.service';
import { MeService } from '../me/me.service';
import { Router } from '@angular/router';
import { Api } from '../api/api';
import { createFakeTokenDto, isFakeBackendEnabled } from '../../utils/fake-backend.util';
import { ensureBearerToken } from '../../utils/bearer-token.util';
import { browserNavigation } from '../../utils/browser-navigation.util';
import { getTokenExpiration, isImpersonatedToken } from '../../utils/jwt-claims.util';
import { ToastService } from '../../components/toast/service/toast.service';
import { buildSessionHandoffUrl } from '../../utils/session-handoff.util';
import { isValidMockCredentials } from '../../mocks/interfaces/user.mock';
import { canAccessTenantBackoffice, TENANT_BACKOFFICE_DENIED_MESSAGE } from '../../utils/backoffice-access.util';

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  constructor(
    private readonly router: Router,
    private readonly meService: MeService,
    private readonly localStorage: LocalStorage,
  ) { }

  private readonly api: Api = new Api();

  private stopRequest: Promise<void> | null = null;

  private refreshRequest: Promise<boolean> | null = null;

  private readonly toastService: ToastService = inject(ToastService);

  public async login(payload: AuthPayload): Promise<void> {
    if (isFakeBackendEnabled()) {
      await this.loginMocked(payload);
      return;
    }

    const response = await this.api.postAsync<TokenDto>(environment.routes.auth.login, payload);

    if (response) {
      this.saveToken(response);
      await this.meService.storageMe();
      this.assertBackofficeAccess();
    }
  }

  public async loginMocked(payload: AuthPayload): Promise<void> {
    if (!isValidMockCredentials(payload.username, payload.password)) {
      throw Object.assign(new Error('Credenciais inválidas.'), {
        error: { message: 'Credenciais inválidas.' },
      });
    }

    this.saveToken(createFakeTokenDto());
    await this.meService.storageMeMocked();
    this.assertBackofficeAccess();
  }

  public async refreshToken(): Promise<boolean> {
    // Requisições simultâneas com o token expirado compartilham o mesmo refresh
    this.refreshRequest ??= this.requestRefreshToken()
      .catch(() => false)
      .finally(() => {
        this.refreshRequest = null;
      });

    return this.refreshRequest;
  }


  public async refreshTokenMocked(): Promise<boolean> {
    this.saveToken(createFakeTokenDto());
    return true;
  }

  public async requestPasswordReset(email: string): Promise<void> {
    if (isFakeBackendEnabled()) {
      return this.requestPasswordResetMocked(email);
    }

    await this.api.postAsync(environment.routes.auth.forgotPassword, { email });
  }

  public async requestPasswordResetMocked(email: string): Promise<void> {
    if (!email.trim()) {
      throw new Error('E-mail é obrigatório.');
    }
  }

  public async resetPassword(password: string, confirmPassword: string, resetPasswordCode: string): Promise<boolean> {
    if (isFakeBackendEnabled()) {
      return this.resetPasswordMocked(password, confirmPassword, resetPasswordCode);
    }

    const response = await this.api.postAsync<{ ok: boolean }>(
      environment.routes.auth.resetPassword,
      {
        resetPasswordCode,
        newPassword: password,
        confirmPassword,
      },
    );

    return !!response;
  }

  public async resetPasswordMocked(password: string, confirmPassword: string, resetPasswordCode = 'MOCKCODE'): Promise<boolean> {
    if (!resetPasswordCode?.trim()) {
      throw Object.assign(new Error('O código de redefinição é inválido'), {
        error: { message: 'O código de redefinição é inválido' },
      });
    }

    if (!password?.trim() || password !== confirmPassword) {
      throw Object.assign(new Error('As senhas não coincidem'), {
        error: { message: 'As senhas não coincidem' },
      });
    }

    return true;
  }

  public async logout(): Promise<void> {
    this.localStorage.clear();
    await this.router.navigateByUrl('/login');
  }

  public isLoggedIn(): boolean {
    if (!this.getToken())
      return false;

    // Sessão com refresh token vencido não pode ser renovada: limpa para o login não reaproveitar o token
    if (this.isSessionExpired()) {
      this.clearSession();
      return false;
    }

    return true;
  }

  public isSessionExpired(now: number = Date.now()): boolean {
    const token = this.getToken();

    if (!token)
      return false;

    if (!token.refreshToken)
      return true;

    const expiresAt = getTokenExpiration(token.refreshToken) ?? Date.parse(token.refreshExpiresAt);

    return !Number.isNaN(expiresAt) && expiresAt <= now;
  }

  public async expireSession(): Promise<void> {
    if (!this.getToken())
      return;

    this.clearSession();
    this.toastService.warn('Sua sessão expirou. Faça login novamente.', 'Sessão encerrada');
    await this.router.navigateByUrl(environment.configs.unauthenticatedRoute, { replaceUrl: true });
  }

  public canAccessBackoffice(): boolean {
    return canAccessTenantBackoffice(this.meService.getMe());
  }

  public clearSession(): void {
    this.localStorage.clear();
  }

  public getToken(): TokenDto | null {
    return this.localStorage.getItem(environment.keys.token);
  }

  public isImpersonating(): boolean {
    return isImpersonatedToken(this.getToken()?.token);
  }

  public async acceptImpersonation(token: TokenDto): Promise<void> {
    this.saveToken(token);
    await this.meService.storageMe();
    this.assertBackofficeAccess();
  }

  public async stopImpersonation(): Promise<void> {
    if (this.stopRequest) {
      return this.stopRequest;
    }

    this.stopRequest = this.executeStopImpersonation().finally(() => {
      this.stopRequest = null;
    });

    return this.stopRequest;
  }

  private async requestRefreshToken(): Promise<boolean> {
    if (isFakeBackendEnabled()) {
      return this.refreshTokenMocked();
    }

    const current = this.getToken();

    if (!current?.refreshToken) {
      return false;
    }

    const api = new Api();

    const token = await api.postAsync<TokenDto>(
      environment.apiUrl + environment.routes.auth.refresh,
      {},
      {
        cancelInterceptors: true,
        headers: {
          Authorization: ensureBearerToken(current.refreshToken),
        },
      },
    );

    if (token)
      this.saveToken(token);

    return !!token;
  }

  private async executeStopImpersonation(): Promise<void> {
    const response = await this.api.postAsync<TokenDto>(environment.routes.auth.impersonateStop, {});

    if (!response?.token || !response.refreshToken) {
      throw Object.assign(new Error('Não foi possível encerrar a impersonação.'), {
        error: { message: 'Não foi possível encerrar a impersonação.' },
      });
    }

    this.localStorage.remove(environment.keys.token);
    this.localStorage.remove(environment.keys.user);
    browserNavigation.assign(buildSessionHandoffUrl(
      `${environment.apps.master.replace(/\/$/, '')}/session/restore`,
      response,
    ));
  }

  private assertBackofficeAccess(): void {
    if (this.canAccessBackoffice()) {
      return;
    }

    this.clearSession();
    throw Object.assign(new Error(TENANT_BACKOFFICE_DENIED_MESSAGE), {
      error: { message: TENANT_BACKOFFICE_DENIED_MESSAGE },
    });
  }

  private saveToken(token: TokenDto): void {
    this.localStorage.setItem(environment.keys.token, {
      ...token,
      token: ensureBearerToken(token.token),
      refreshToken: ensureBearerToken(token.refreshToken),
    });
  }

}
